import { useCallback, useEffect, useMemo } from 'react';

import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import type { ColumnConfig, ColumnVisibilityState } from './types';

type UseColumnVisibilityProps<T extends string = string> = {
  defaultColumnOrder: T[];
  defaultVisibleColumns: T[];
  allColumnsMap: Record<T, { title: string }>;
  columnOrderParamKey?: string;
  visibleColumnsParamKey?: string;
};

export const useColumnVisibility = <T extends string = string>({
  defaultColumnOrder,
  defaultVisibleColumns,
  allColumnsMap,
  columnOrderParamKey = 'columnOrder',
  visibleColumnsParamKey = 'columns',
}: UseColumnVisibilityProps<T>) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const getLocalStorageKey = useCallback(() => {
    return `${location.pathname}-column`;
  }, [location.pathname]);

  const handleSaveToLocalStorage = useCallback(
    (order: T[], visible: T[]) => {
      const storageKey = getLocalStorageKey();
      const storageData: { columnOrder: T[]; visibleColumns: T[] } = {
        columnOrder: order,
        visibleColumns: visible,
      };
      localStorage.setItem(storageKey, JSON.stringify(storageData));
    },
    [getLocalStorageKey]
  );

  const getLocalStorageValue = useCallback((): {
    columnOrder: T[] | undefined;
    visibleColumns: T[] | undefined;
  } => {
    const storageKey = getLocalStorageKey();
    const storageValue = localStorage.getItem(storageKey);
    if (!storageValue) return { columnOrder: undefined, visibleColumns: undefined };

    try {
      const parsed = JSON.parse(storageValue) as { columnOrder: T[]; visibleColumns: T[] };
      return {
        columnOrder: parsed.columnOrder || undefined,
        visibleColumns: parsed.visibleColumns || undefined,
      };
    } catch {
      return { columnOrder: undefined, visibleColumns: undefined };
    }
  }, [getLocalStorageKey]);

  const columnOrderParam = searchParams.get(columnOrderParamKey);
  const visibleColumnsParam = searchParams.get(visibleColumnsParamKey);

  const columnOrder = useMemo<T[]>(() => {
    if (columnOrderParam) return columnOrderParam.split(',') as T[];

    const stored = getLocalStorageValue();
    if (stored.columnOrder) return stored.columnOrder;

    return defaultColumnOrder;
  }, [columnOrderParam, defaultColumnOrder, getLocalStorageValue]);

  const visibleColumns = useMemo<T[]>(() => {
    if (visibleColumnsParam) return visibleColumnsParam.split(',') as T[];

    const stored = getLocalStorageValue();
    if (stored.visibleColumns) return stored.visibleColumns;

    return defaultVisibleColumns;
  }, [visibleColumnsParam, defaultVisibleColumns, getLocalStorageValue]);

  const columnConfigs = useMemo<ColumnConfig<T>[]>(() => {
    return columnOrder.map((key) => ({
      key,
      title: allColumnsMap[key]?.title || String(key),
      visible: visibleColumns.includes(key),
    }));
  }, [columnOrder, visibleColumns, allColumnsMap]);

  const allColumnsVisible = visibleColumns.length === defaultColumnOrder.length;

  const handleOrderChange = useCallback(
    (newOrder: T[]) => {
      searchParams.set(columnOrderParamKey, newOrder.join(','));
      setSearchParams(searchParams);
      handleSaveToLocalStorage(newOrder, visibleColumns);
    },
    [searchParams, setSearchParams, columnOrderParamKey, visibleColumns, handleSaveToLocalStorage]
  );

  const handleVisibilityChange = useCallback(
    (columnKey: T, visible: boolean) => {
      const newVisibleColumns = visible
        ? [...visibleColumns, columnKey]
        : visibleColumns.filter((key) => key !== columnKey);

      const newVisibleColumnsSet = new Set(newVisibleColumns);
      const defaultVisibleColumnsSet = new Set(defaultVisibleColumns);
      const isSameAsDefault =
        newVisibleColumnsSet.size === defaultVisibleColumnsSet.size &&
        [...newVisibleColumnsSet].every((key) => defaultVisibleColumnsSet.has(key));

      if (isSameAsDefault) {
        searchParams.delete(visibleColumnsParamKey);
      } else {
        searchParams.set(visibleColumnsParamKey, newVisibleColumns.join(','));
      }
      setSearchParams(searchParams);
      handleSaveToLocalStorage(columnOrder, newVisibleColumns);
    },
    [
      visibleColumns,
      defaultVisibleColumns,
      searchParams,
      setSearchParams,
      handleSaveToLocalStorage,
      columnOrder,
      visibleColumnsParamKey,
    ]
  );

  const handleToggleVisibility = useCallback(
    (columnKey: T) => {
      const isVisible = visibleColumns.includes(columnKey);
      handleVisibilityChange(columnKey, !isVisible);
    },
    [visibleColumns, handleVisibilityChange]
  );

  const handleShowAll = useCallback(() => {
    const defaultVisibleColumnsString = defaultVisibleColumns.join(',');
    setSearchParams({ visibleColumnsParamKey: defaultVisibleColumnsString });

    const storageKey = getLocalStorageKey();
    localStorage.removeItem(storageKey);
  }, [defaultVisibleColumns, setSearchParams, getLocalStorageKey]);

  const state: ColumnVisibilityState<T> = {
    columnOrder,
    visibleColumns,
  };

  useEffect(() => {
    const storageValue = getLocalStorageValue();
    if (!storageValue.visibleColumns) return;

    const { visibleColumns } = storageValue;
    navigate(`?${visibleColumnsParamKey}=${visibleColumns.join(',')}`);
  }, [navigate, location.pathname, visibleColumnsParamKey, getLocalStorageValue]);

  return {
    state,
    columnConfigs,
    allColumnsVisible,
    handleOrderChange,
    handleVisibilityChange,
    handleToggleVisibility,
    handleShowAll,
  };
};
